import { Link } from 'react-router-dom';
import type { ReplayItem } from '@/mocks/replays';
import { formatCount, weekdayOf } from '@/utils/time';

interface ReplayCardProps {
  item: ReplayItem;
  onOpen?: (id: string) => void;
  active?: boolean;
}

export default function ReplayCard({ item, onOpen, active = false }: ReplayCardProps) {
  const body = (
    <>
      <div className="relative aspect-[16/10] w-full overflow-hidden rounded-md bg-background-200">
        <img
          src={item.cover}
          alt={`爆料直播网 - ${item.title} 回放封面`}
          title={`${item.title} 直播回放列表`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/55 via-transparent to-transparent" />
        <span className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background-50/90 text-foreground-900">
          <i className="ri-play-fill text-base" aria-hidden="true" />
        </span>
        <span className="absolute bottom-3 right-3 rounded bg-foreground-950/70 px-2 py-0.5 text-[11px] text-background-100">
          {item.duration}
        </span>
      </div>

      <div className="mt-3 flex flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded bg-secondary-100 px-2 py-0.5 font-medium text-secondary-900">
            {item.category}
          </span>
          <span className="text-foreground-500">
            {item.dateLabel} {weekdayOf(item.dateLabel)}
          </span>
        </div>
        <h3 className="mt-2 font-heading text-[15px] font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          {item.title}
        </h3>
        <p
          className={`mt-2 text-xs leading-relaxed text-foreground-600 ${
            active ? '' : 'line-clamp-2'
          }`}
        >
          {item.summary}
        </p>
        <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
          <span>主播 {item.host}</span>
          <span className="text-foreground-300">·</span>
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-play-circle-line" aria-hidden="true" />
            </span>
            {formatCount(item.plays)} 次播放
          </span>
        </div>

        {active && (
          <dl className="mt-3 grid grid-cols-2 gap-3 rounded-md border border-background-200 bg-background-100 p-3 text-xs">
            <div>
              <dt className="text-foreground-500">所属栏目</dt>
              <dd className="mt-1 font-medium text-foreground-900">{item.category}</dd>
            </div>
            <div>
              <dt className="text-foreground-500">回放日期</dt>
              <dd className="mt-1 font-medium text-foreground-900">
                {item.dateLabel} {weekdayOf(item.dateLabel)}
              </dd>
            </div>
            <div>
              <dt className="text-foreground-500">回放时长</dt>
              <dd className="mt-1 font-medium text-foreground-900">{item.duration}</dd>
            </div>
            <div>
              <dt className="text-foreground-500">播放量</dt>
              <dd className="mt-1 font-medium text-foreground-900">{formatCount(item.plays)} 次</dd>
            </div>
          </dl>
        )}

        {onOpen && (
          <span className="mt-3 flex items-center gap-1 text-xs font-medium text-primary-700">
            {active ? '收起回放信息' : '展开回放信息'}
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className={active ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} aria-hidden="true" />
            </span>
          </span>
        )}
      </div>
    </>
  );

  if (onOpen) {
    return (
      <article className="group flex h-full flex-col">
        <button
          type="button"
          onClick={() => onOpen(item.id)}
          className="flex h-full flex-col text-left"
          aria-expanded={active}
        >
          {body}
        </button>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col">
      <Link to="/replay" className="flex h-full flex-col" aria-label={item.title}>
        {body}
      </Link>
    </article>
  );
}